import { useMount, useUnmount, useUpdateEffect } from 'ahooks';
import { uniqueId } from 'lodash-es';
import React, { ReactNode } from 'react';
import { Box } from '@apia/theme';
import store from '../../../../store';
import { responsiveTableActions } from '../../../../store/responsiveTableSlice';
import {
  defaultLabels,
  TResponsiveTableContext,
  TResponsiveTableContextProps,
} from './types';

const contexts: Record<string, TResponsiveTableContext> = {};

export const ResponsiveTableReactContext =
  React.createContext<TResponsiveTableContext>({} as TResponsiveTableContext);

export function useResponsiveTableContext(tableName?: string) {
  let context: TResponsiveTableContext | null = tableName
    ? contexts[tableName]
    : null;
  const rContext = React.useContext(ResponsiveTableReactContext);
  if (!context) context = rContext;
  if (!context) throw new Error('There is no ResponsiveTableContext');
  return context;
}

export const ResponsiveTableContextReproducer = ({
  children,
  tableName,
}: {
  children: ReactNode;
  tableName: string;
}) => {
  return (
    <ResponsiveTableReactContext.Provider value={contexts[tableName]}>
      {children}
    </ResponsiveTableReactContext.Provider>
  );
};

/**
 * Este contexto busca facilitar la tarea de centralizar las
 * propiedades y callbacks asociados a la tabla, de forma de
 * evitar la necesidad de distribuir comportamientos.
 *
 * **MUY IMPORTANTE**
 * Asegurarse de memoizar las propiedades rows, filters y
 * columns, ya que en caso contrario eso va a disparar
 * actualizaciones en el store cada vez que el padre del
 * contexto renderice.
 *
 */
const ResponsiveTableContext = ({
  allowEdition,
  allowRowsKeyboardSorting,
  allowSelection,
  allowSorting,
  avoidReparseSelectionOnRowChange,
  avoidTableDestroyOnUnmount,
  children,
  customLabels,
  columns,
  filters,
  rows,
  label,
  isMultiple,
  name,
  onChangeSelection,
  onFilterBlur,
  onFilterChange,
  onFilterError: onError,
  onFilterPressEnter,
  onSelectRows,
  onSortChange,
  reserveColumnsForStates,
  variants,
}: TResponsiveTableContextProps) => {
  const actualName = React.useMemo(
    () => name ?? uniqueId('responsiveTable'),
    [name],
  );
  const labels = React.useMemo(
    () => ({ ...defaultLabels, ...customLabels }),
    [customLabels],
  );
  const contextValue = React.useMemo<TResponsiveTableContext>(
    () => ({
      label,
      labels,
      name: actualName,
      onChangeSelection,
      onFilterBlur,
      onFilterChange,
      onFilterPressEnter,
      onSelectRows,
      onSortChange,
      onFilterError: onError,
      variants,
    }),
    [
      actualName,
      label,
      labels,
      onChangeSelection,
      onFilterBlur,
      onFilterChange,
      onFilterPressEnter,
      onSelectRows,
      onSortChange,
      onError,
      variants,
    ],
  );
  contexts[actualName] = contextValue;

  useMount(() => {
    store.dispatch(
      responsiveTableActions.update({
        allowEdition,
        allowRowsKeyboardSorting,
        allowSelection,
        allowSorting,
        columns,
        filters: filters ?? [],
        rows,
        isMultiple,
        tableName: actualName,
        reserveColumnsForStates,
      }),
    );
  });

  useUpdateEffect(() => {
    store.dispatch(
      responsiveTableActions.update({
        tableName: actualName,
        columns,
      }),
    );
  }, [columns]);

  useUpdateEffect(() => {
    store.dispatch(
      responsiveTableActions.update({
        tableName: actualName,
        rows,
      }),
    );
    if (avoidReparseSelectionOnRowChange !== true) {
      const selected = rows
        .map((current, i) => ({ ...current, i }))
        .filter((current) => current.initiallySelected);
      store.dispatch(
        responsiveTableActions.update({
          tableName: actualName,
          selectedRows: selected.map((current) => current.i),
        }),
      );
    }
  }, [rows]);

  useUpdateEffect(() => {
    store.dispatch(
      responsiveTableActions.update({
        tableName: actualName,
        filters,
      }),
    );
  }, [filters]);

  useUnmount(() => {
    if (!avoidTableDestroyOnUnmount) {
      store.dispatch(responsiveTableActions.destroy({ tableName: actualName }));
    }
  });

  return (
    <ResponsiveTableReactContext.Provider value={contextValue}>
      <Box id={name} className="responsiveTableContext__wrapper">
        {children}
      </Box>
    </ResponsiveTableReactContext.Provider>
  );
};

export default React.memo(ResponsiveTableContext);
